import { defaultLiveBreakWidgetCopy } from "@loot-os/api-contract";
import { LiveBreaksWidget } from "@loot-os/design-system/domain/liveBreaks/LiveBreaksWidget";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { widgetStreamToWatchable } from "../../lib/liveBreaksWidget.js";
import { useWidgetLift } from "../../lib/liveBreaksWidgetLift.js";
import { setWidgetRowCollapsed, useWidgetCollapsed } from "../../lib/liveBreaksWidgetPrefs.js";
import { hideStreamRow, openPip, pinStream, selectPromotedStream } from "../../lib/pip.js";
import { useTerminology } from "../../lib/terminology.js";
import { useTwitchLinkReturn } from "../../lib/twitchConnection.js";
import { useLiveBreaksWidget } from "../../lib/useLiveBreaksWidget.js";
import { LiveBreakPip } from "./LiveBreakPip.js";

/**
 * The live-breaks widget (docs/live-breaks.md, "Storefront"; boxed's `LiveBreaksDock`):
 * the store's bottom-left column, drawn once beside the frame so navigating never remounts it,
 * with the picture-in-picture player on top and the widget's rows under it. On a live break's page
 * the rows hide and the player alone stays. Nothing shows without the store's live breaks.
 */
export function LiveBreaksDock() {
  const widget = useLiveBreaksWidget();
  const words = defaultLiveBreakWidgetCopy(useTerminology());
  const { rows, pip, twitch } = widget;
  const { collapsed, setCollapsed } = useWidgetCollapsed(rows.stream?.key);
  const playerOpen = twitch && pip.stream !== null;
  const pathname = useLocation({ select: (location) => location.pathname });
  // The PiP player's Sign in to Twitch, when the browser sent the whole tab instead of a popup.
  useTwitchLinkReturn({
    handledByPage: !twitch || widget.activeLiveBreakId !== null || pathname === "/account",
  });

  // Opening a live break in the player unfolds its row, so it is open again once the player closes.
  const playing = pip.stream?.liveBreakId ?? null;
  useEffect(() => {
    if (playing !== null) setWidgetRowCollapsed(playing, false);
  }, [playing]);

  // On a phone the support chat button clears the rows (never the player, which the shopper moves).
  const wrapper = useRef<HTMLDivElement>(null);
  useWidgetLift(
    wrapper,
    widget.enabled &&
      (rows.hosting !== null || rows.queue !== null || (rows.stream !== null && !collapsed)),
  );

  if (!widget.enabled) return null;
  return (
    // `contents`: no box of its own, so the widget is placed as if it had no wrapper.
    <div ref={wrapper} className="contents">
      <LiveBreaksWidget
        words={words}
        stream={rows.stream}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        picker={rows.picker}
        onPick={(stream) => {
          // A live break is pinned; a promoted stream, which has none, is picked instead.
          if (stream.liveBreak) pinStream(widgetStreamToWatchable(stream));
          else selectPromotedStream(stream.channel);
        }}
        onOpenPip={twitch ? (stream) => openPip(widgetStreamToWatchable(stream)) : undefined}
        onHide={hideStreamRow}
        hosting={rows.hosting}
        queue={rows.queue}
        pip={
          playerOpen && pip.stream ? (
            <LiveBreakPip
              stream={pip.stream}
              words={words}
              activeLiveBreakId={widget.activeLiveBreakId}
            />
          ) : null
        }
      />
    </div>
  );
}
